import { AbstractGameController } from "@lib/titan2/dist/game/controller/AbstractGameController";
import { EventObject } from "@lib/titan2/dist/util/event/EventObject";
import { gsap } from "gsap";
import { FeatureTransitionEvents } from "./FeatureTransitionController";
import { FeatureGoldFishEvents } from "../view/abstract/display/features/goldfish/FeatureGoldFish";
import { GameBigWinEvents } from "../view/abstract/display/GameBigWin";
import { Service } from "@lib/titan2-slingo/dist/api/Service";
import { GameEvent } from "./event/GameEvent";
import { SeededRandom } from "../utils/SeededRandom";
import { MainTransitionEvents } from "../view/abstract/display/GameMainTransition";
export class FeatureGoldFishController extends AbstractGameController {
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(FeatureGoldFishControllerEvents.BEGIN_FEATURE, this.handleBeginFeature);
    }
    async handleBeginFeature() {
        const availableYarnIndexes = Array.from({ length: 5 }, (x, i) => i);
        const seededRandomUserChoice = new SeededRandom(this.model.getGameInstanceId());
        const data = this.model.getGoldFishChanceGameData();
        this.dispatchGame(FeatureGoldFishEvents.START);
        this.dispatchGame(new EventObject(FeatureGoldFishEvents.START_GAME_INTRO.getType(), { baseGoldFish: data.getBaseGoldFish() }));
        this.dispatchGame(new EventObject(FeatureGoldFishEvents.UPDATE_SPIN_COUNT.getType(), { count: data.getBaseSpins() }));
        await this.blockUntil(FeatureGoldFishEvents.PICKS_READY);
        let index;
        if (Service.isReplay()) {
            await this.delayFor(1);
            index = this.model.consumeNextUserChoice();
            if (index === null) {
                index = seededRandomUserChoice.pickFromArray(availableYarnIndexes);
            }
        }
        else {
            index = (await this.blockUntil(FeatureGoldFishEvents.PICK_SELECTED)).getPayload()["index"];
            this.dispatchGame(GameEvent.createUserchoiceMadeEvent({ choice: index }));
        }
        this.dispatchGame(new EventObject(FeatureGoldFishEvents.REVEAL_PRIZES.getType(), { index, reward: data.getPickReward(), missedRewards: data.getMissedPickRewards(), spinCount: data.getSpinCount() }));
        await this.blockUntil(FeatureGoldFishEvents.REVEAL_PRIZES_COMPLETE);
        await this.delayFor(0.5);
        const spins = data.getSpinsData();
        for (const [spinIndexString, spin] of Object.entries(spins)) {
            const spinIndex = parseInt(spinIndexString);
            this.dispatchGame(new EventObject(FeatureGoldFishEvents.UPDATE_SPIN_COUNT.getType(), { count: spin.spinsRemaining }));
            this.dispatchGame(new EventObject(FeatureGoldFishEvents.SPIN.getType(), { results: spin.type, payouts: spin.payout, lockedReels: spin.lockedReels, spinType: spin.spinType }));
            await this.blockUntil(FeatureGoldFishEvents.SPIN_ANIMATION_COMPLETE);
            if (spin.spinWin > 0) {
                await this.delayFor(0.5);
                this.dispatchGame(new EventObject(FeatureGoldFishEvents.REVEAL_REEL_PAYOUTS.getType(), { payouts: spin.unmultipiedPayout }));
                await this.delayFor(0.5);
            }
            else {
                await this.delayFor(0.3);
            }
            await this.collectGoldFish(spin);
            await this.collectBlueFish(spin);
            await this.distributeMultiplier(spin);
            await this.collectReelPayouts(spin);
            if (spin.respinTriggered) {
                await this.lockReels(spin, spins[spinIndex - 1]);
            }
            else if (spin.spinType === "respin") {
                await this.exitRespins(spin);
            }
        }
        await this.delayFor(2);
        this.dispatchGame(FeatureTransitionEvents.TRANSITION_WIN_PARTICLE);
        this.dispatchGame(GameBigWinEvents.PLAY);
        await this.blockUntil(GameBigWinEvents.COMPLETE);
        this.dispatchGame(FeatureTransitionEvents.TRANSITION_BACK_TO_BASE_GAME);
        await this.blockUntil(MainTransitionEvents.BLACKOUT_MID_POINT);
        this.dispatchGame(FeatureGoldFishEvents.CLEAR);
    }
    async collectBlueFish(spin) {
        if (!spin.respinTriggered) {
            return;
        }
        const blueReels = spin.type
            .map((type, reelIndex) => ({ type, reelIndex }))
            .filter(({ type }) => type === "BLUE")
            .map(({ type, reelIndex }, index) => ({ type, reelIndex, blueFishIndex: index }))
            .map(({ reelIndex, blueFishIndex }, index) => ({ reelIndex, blueFishIndex, multiplier: spin.multipliers[blueFishIndex], index }));
        for (const { reelIndex, multiplier, index } of blueReels) {
            const isLast = index === blueReels.length - 1;
            this.dispatchGame(new EventObject(FeatureGoldFishEvents.COLLECT_WATCH_FROM_REEL.getType(), { reelIndex, multiplier, isLast }));
            await this.blockUntil(FeatureGoldFishEvents.COLLECT_WATCH_FROM_REEL_COMPLETE);
        }
    }
    async distributeMultiplier(spin) {
        if (spin.blueFishCounter < 2) {
            return;
        }
        this.dispatchGame(new EventObject(FeatureGoldFishEvents.DISTRIBUTE_MULTIPLIER.getType(), { unmultipliedPayout: spin.unmultipiedPayout, multipliedPayout: spin.multipliedPayout }));
        await this.blockUntil(FeatureGoldFishEvents.DISTRIBUTE_MULTIPLIER_COMPLETE);
    }
    async lockReels(spin, previousSpin) {
        const lockingReels = spin.type
            .map((type, reelIndex) => ({ type, reelIndex }))
            .filter(({ type }) => !["BLUE", "GOLD", "BLANK"].includes(type))
            .filter(({ reelIndex }) => !previousSpin?.lockedReels.includes(reelIndex))
            .map(({ reelIndex }) => reelIndex);
        this.dispatchGame(new EventObject(FeatureGoldFishEvents.ENABLE_LOCKS.getType(), { lockingReels }));
    }
    async exitRespins(spin) {
        this.dispatchGame(FeatureGoldFishEvents.EXIT_RESPINS);
        await this.blockUntil(FeatureGoldFishEvents.EXIT_RESPINS_COMPLETE);
    }
    async collectGoldFish(spin) {
        const goldFishReels = spin.type
            .map((type, reelIndex) => ({ type, reelIndex }))
            .filter(type => type.type === "GOLD");
        const goldFishCollected = spin.goldFishCounter - spin.goldFishCounterBeforeSpin;
        for (let goldFishCollection = 0; goldFishCollection < goldFishCollected; goldFishCollection++) {
            const reelIndex = goldFishReels[goldFishCollection].reelIndex;
            this.dispatchGame(new EventObject(FeatureGoldFishEvents.COLLECT_CAT_FROM_REEL.getType(), { reelIndex }));
            await this.blockUntil(FeatureGoldFishEvents.COLLECT_CAT_FROM_REEL_COMPLETE);
        }
    }
    async collectReelPayouts(spin) {
        if (spin.spinWin === 0) {
            return;
        }
        const goldfishCount = spin.cumulativeSpinWins.length;
        for (const [indexString, cumulativeSpinWin] of Object.entries(spin.cumulativeSpinWins)) {
            const goldFishIndex = parseInt(indexString);
            this.dispatchGame(new EventObject(FeatureGoldFishEvents.COLLECT_REEL_PAYOUT.getType(), {
                goldfishCount,
                goldFishIndex,
                cumulativePayout: spin.cumulativeSpinWins[goldFishIndex],
                reelPayouts: spin.payout,
                isLast: goldFishIndex === spin.cumulativeSpinWins.length - 1,
                payoutFrom: goldFishIndex === 0 ? 0 : spin.spinWinStages[goldFishIndex - 1],
                payoutTo: spin.spinWinStages[goldFishIndex]
            }));
            await this.blockUntil(FeatureGoldFishEvents.COLLECT_REEL_PAYOUT_COMPLETE);
        }
    }
    createModel() {
        super.createModel();
        this.model = this.baseModel;
    }
    async blockUntil(event) {
        return new Promise(resolve => {
            this.registerGame(event, (recievedEvent) => {
                this.unregisterGame(event);
                resolve(recievedEvent);
            });
        });
    }
    async delayFor(timeInSeconds) {
        return new Promise(resolve => {
            gsap.delayedCall(timeInSeconds, resolve);
        });
    }
}
export class FeatureGoldFishControllerEvents {
}
FeatureGoldFishControllerEvents.BEGIN_FEATURE = new EventObject("FeatureGoldFishControllerEvents.BEGIN_FEATURE");
//# sourceMappingURL=FeatureGoldFishController.js.map